import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ScanEye } from 'lucide-react';
import { investorProductScenario } from '../../data/investorProductScenario';
import { DemoWorkspace } from '../DemoWorkspace';
import { useInvestorReducedMotion } from './useInvestorReducedMotion';

const investorReel = [
  { scene: 0, verb: 'UNDERSTAND', title: 'Understand the investor question', explanation: 'Intent enters one operating context before any agent acts.' },
  { scene: 1, verb: 'GROUND', title: 'Ground the company context', explanation: 'Product, evidence, people, and policy resolve into one graph.' },
  { scene: 2, verb: 'PLAN', title: 'Compile the diligence workflow', explanation: 'Specialists receive bounded tasks, tools, and checkpoints.' },
  { scene: 3, verb: 'WRITE', title: 'Write the evidence brief', explanation: 'Claims and citations become one reviewable decision document.' },
  { scene: 4, verb: 'RETRIEVE', title: 'Retrieve trusted knowledge', explanation: 'Private context returns with permissions and sources attached.' },
  { scene: 5, verb: 'REVIEW', title: 'Coordinate the review', explanation: 'People and agents work together without hiding the approval line.' },
  { scene: 6, verb: 'VERIFY', title: 'Verify in a sandbox', explanation: 'Bounded code tests claims without production write access.' },
  { scene: 7, verb: 'INSPECT', title: 'Inspect current evidence', explanation: 'Approved external sources enter the same cited context.' },
  { scene: 8, verb: 'PERSIST', title: 'Persist the decision state', explanation: 'Owners, tasks, approvals, and outcomes survive the run.' },
  { scene: 9, verb: 'REMEMBER', title: 'Keep the meeting memory', explanation: 'Decisions and follow-ups are captured while the discussion happens.' },
  { scene: 10, verb: 'COMPOSE', title: 'Compose the investor story', explanation: 'Slides, images, and product scenes become a reviewable brief.' }
];

/**
 * InvestorShowcase — interactive replacement for the auto-play
 * InvestorProductLoop. Same 11 product surfaces, but the investor picks
 * chapters: narration (capability + explanation) on the left, sticky MacBook
 * running that surface on the right. No timers, no lid open/close loop, no
 * auto-advance — the reel no longer plays past the buyer.
 */
export const InvestorShowcase = () => {
  const reduced = useInvestorReducedMotion();
  const [frameIndex, setFrameIndex] = useState(0);
  const frame = investorReel[frameIndex];

  return (
    <aside className="investor-showcase" aria-label="Interactive investor product showcase" data-testid="investor-product-demo-loop">
      <div className="journey-showcase investor-showcase-grid" data-testid="investor-showcase">
        <div className="journey-chapters" data-testid="investor-showcase-chapters">
          <p className="journey-kicker">STORY / INVESTOR PRODUCT REEL</p>
          <nav aria-label="Investor product chapters" className="journey-chapter-rail">
            {investorReel.map((item, index) => (
              <button
                key={item.verb}
                type="button"
                className={index === frameIndex ? 'active' : index < frameIndex ? 'passed' : ''}
                onClick={() => setFrameIndex(index)}
                aria-current={index === frameIndex ? 'true' : undefined}
                data-testid={`investor-showcase-${item.verb.toLowerCase()}-button`}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <b>{item.verb}</b>
              </button>
            ))}
          </nav>
          <AnimatePresence mode="wait">
            <motion.div
              key={frame.scene}
              className="journey-chapter"
              initial={reduced ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduced ? undefined : { opacity: 0, y: -6 }}
              transition={{ duration: reduced ? 0 : 0.2, ease: [0.215, 0.61, 0.355, 1] }}
              data-testid="investor-loop-explanation"
            >
              <span>{String(frame.scene + 1).padStart(2, '0')} / 11 · INVESTOR PRODUCT REEL</span>
              <strong data-testid="investor-loop-explanation-title">{frame.title}</strong>
              <p data-testid="investor-loop-explanation-copy">{frame.explanation}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="journey-macbook" data-testid="investor-loop-macbook">
          <div className="journey-macbook-sticky">
            <div className="laptop-film-shell journey-shell">
              <div className="investor-loop-transparency" role="img" aria-label="Transparent, explainable product run" title="Transparent product run" data-testid="investor-transparency-icon">
                <ScanEye size={14} strokeWidth={1.7} />
              </div>
              <div className="laptop-film-lid" data-testid="investor-loop-macbook-lid">
                <div className="laptop-film-camera" aria-hidden="true" />
                <div className="laptop-film-screen" data-testid="investor-loop-product-screen">
                  <DemoWorkspace compact showcase scenarioOverride={investorProductScenario} activeSceneIndex={frame.scene} />
                </div>
              </div>
              <div className="laptop-film-base" aria-hidden="true"><i /></div>
            </div>
            <p className="laptop-modeled-label">MODELED INVESTOR SCENARIO / LIVE PRODUCT SURFACE</p>
          </div>
        </div>
      </div>
    </aside>
  );
};
